import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Skeleton } from '@ibsys/kit';
import { defaultThreshold, isThresholdApplied, useStrategistTransitions } from '@/entities/strategist';
import type { StrategistTransitionsRequest } from '@/entities/strategist';
import { ConceptCardDrawer, useOpenConceptCard } from '@/features/concept-card';
import { DialogsDrawerContent, useOpenDialogsDrawer } from '@/features/dialogs-drawer';
import { PageContainer } from '@/shared/ui';
import { StrategistFilters } from '@/widgets/StrategistFilters';
import type { StrategistFiltersValue } from '@/widgets/StrategistFilters';
import type { StrategistTransition } from '@/entities/strategist';
import { conceptLabel, DEFAULT_MIN_DIALOGS, filterByInfluence, RELIABLE_THRESHOLD, sortTransitions } from './lib';
import type { InfluenceFilter, TransitionSort } from './lib';
import { TransitionsControls } from './TransitionsControls';
import { TransitionsKpi } from './TransitionsKpi';
import { TransitionsRanking } from './TransitionsRanking';
import { TransitionsSankey } from './TransitionsSankey';

// Сколько строк показывать в каждом рейтинг-списке (повышающие / понижающие).
const RANK_LIMIT = 8;

/**
 * Страница «Переходы» раздела «Стратег»: переходы «возражение → аргумент»
 * и их влияние на целевой исход. Держит сквозной фильтр, порог надёжности
 * (`minDialogs`) и параметры вида (сортировка, топ-N, изоляция узла).
 */
export const StrategistTransitionsPage: FC = () => {
  const { t } = useTranslation(['common', 'strategist']);

  // Состояние, влияющее на запрос.
  const [filterValue, setFilterValue] = useState<StrategistFiltersValue>({ filters: [] });
  const [minDialogs, setMinDialogs] = useState(DEFAULT_MIN_DIALOGS);
  // Пороги близости (по возражениям = from / по аргументам = to); из первого ответа.
  const [objectionThreshold, setObjectionThreshold] = useState<number | undefined>(undefined);
  const [argumentThreshold, setArgumentThreshold] = useState<number | undefined>(undefined);

  // Состояние вида (клиентское, запрос не пересчитывает).
  const [sort, setSort] = useState<TransitionSort>('lift');
  const [topN, setTopN] = useState<number>(20);
  const [influence, setInfluence] = useState<InfluenceFilter>('all');
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  const body: StrategistTransitionsRequest = useMemo(
    () => ({
      filters: filterValue.filters,
      dateFrom: filterValue.dateFrom,
      dateTo: filterValue.dateTo,
      minDialogs: minDialogs,
      objectionThreshold: objectionThreshold,
      argumentThreshold: argumentThreshold,
    }),
    [filterValue, minDialogs, objectionThreshold, argumentThreshold],
  );

  const { data, isLoading, isPlaceholderData } = useStrategistTransitions(body);
  const transitions = data?.transitions ?? [];

  // Показывать можно только данные, посчитанные теми же параметрами, что уйдут в срез строки:
  // пока идёт запрос за новыми или пороги ещё не подставлены, страница и панель диалогов разойдутся.
  const isAwaitingData =
    isLoading || isPlaceholderData || !isThresholdApplied(data?.thresholds, [objectionThreshold, argumentThreshold]);

  // Пороги берутся из первого ответа: доступные значения известны только по нему.
  useEffect(() => {
    if (!data) return;
    setObjectionThreshold((prev) => prev ?? defaultThreshold(data.thresholds));
    setArgumentThreshold((prev) => prev ?? defaultThreshold(data.thresholds));
  }, [data]);

  // Отфильтрованные по знаку влияния переходы — общая база для Sankey и рейтингов.
  const visibleTransitions = useMemo(() => filterByInfluence(transitions, influence), [transitions, influence]);

  // Ленты Sankey: выбранная сортировка + ограничение топ-N.
  const sankeyTransitions = useMemo(() => {
    const sorted = sortTransitions(visibleTransitions, sort);
    return topN === 0 ? sorted : sorted.slice(0, topN);
  }, [visibleTransitions, sort, topN]);

  // Рейтинги: по возрастанию влияния на исход (топ по lift) и по убыванию.
  const { rankingUp, rankingDown } = useMemo(() => {
    const byLiftDesc = sortTransitions(visibleTransitions, 'lift');
    return {
      rankingUp: byLiftDesc.filter((tr) => (tr.lift ?? 0) > 0).slice(0, RANK_LIMIT),
      rankingDown: byLiftDesc
        .filter((tr) => (tr.lift ?? 0) < 0)
        .slice(-RANK_LIMIT)
        .reverse(),
    };
  }, [visibleTransitions]);

  const reliableCount = useMemo(() => transitions.filter((tr) => tr.n >= RELIABLE_THRESHOLD).length, [transitions]);

  const handleConceptOpen = useOpenConceptCard(filterValue);

  const openDialogs = useOpenDialogsDrawer(filterValue);
  const handleTransitionOpen = useCallback(
    (transition: StrategistTransition) => {
      openDialogs({
        objectionId: transition.fromConceptId,
        argumentId: transition.toConceptId,
        objectionThreshold,
        argumentThreshold,
        // Срез — сам переход, а не ячейка матрицы: иначе в панель попадут диалоги, где
        // аргумент прозвучал когда-либо позже, и её показатели разойдутся со строкой,
        // из которой открыто, вплоть до обратного знака влияния.
        transition: true,
        title: `${conceptLabel(transition.fromConcept, transition.fromConceptId)} → ${conceptLabel(
          transition.toConcept,
          transition.toConceptId,
        )}`,
      });
    },
    [openDialogs, objectionThreshold, argumentThreshold],
  );

  return (
    <PageContainer className="flex flex-col gap-5">
      <h1 className="text-xl font-semibold text-foreground">{t('common:routes.strategistTransitions')}</h1>

      <StrategistFilters value={filterValue} onChange={setFilterValue} />

      <TransitionsControls
        minDialogs={minDialogs}
        onMinDialogsChange={setMinDialogs}
        sort={sort}
        onSortChange={setSort}
        topN={topN}
        onTopNChange={setTopN}
        influence={influence}
        onInfluenceChange={setInfluence}
        thresholds={data?.thresholds ?? []}
        objectionThreshold={objectionThreshold}
        onObjectionThresholdChange={setObjectionThreshold}
        argumentThreshold={argumentThreshold}
        onArgumentThresholdChange={setArgumentThreshold}
      />

      {isAwaitingData ? (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
          <Skeleton className="h-96 w-full" />
        </div>
      ) : transitions.length === 0 ? (
        <Card>
          <CardContent className="p-10 flex items-center justify-center">
            <p className="text-sm text-muted-foreground">{t('common:emptyTable')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-5">
          <TransitionsKpi
            totalDialogs={data?.totalDialogs ?? 0}
            baselineRate={data?.baselineRate ?? null}
            reliableCount={reliableCount}
          />

          <Card>
            <CardContent className="p-4">
              <TransitionsSankey
                transitions={sankeyTransitions}
                selectedNodeId={selectedNodeId}
                onSelectNode={setSelectedNodeId}
                onConceptClick={handleConceptOpen}
                onTransitionClick={handleTransitionOpen}
              />
            </CardContent>
          </Card>

          <TransitionsRanking
            rankingUp={rankingUp}
            rankingDown={rankingDown}
            onConceptClick={handleConceptOpen}
            onTransitionClick={handleTransitionOpen}
          />
        </div>
      )}
      <ConceptCardDrawer />
      <DialogsDrawerContent />
    </PageContainer>
  );
};
